/* eslint-disable max-lines */
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from 'linaria/react';
import { Adaptive, RegularButton as Button, RegularButton, Typography } from 'fronton-react';
import { Pencil } from 'assets/icons/pencil';
import { RouteContext } from 'entries/common/widgets/context';
import { calculatedRoomsParams, defaultRoomParams, roomArea } from 'pages/dimensions/store/area';
import { gaFocusOnDimensions } from 'ga-events/ga-focus-dimensions';
import { gaClickOnGoods } from 'ga-events/ga-click-goods';
import { getCalculatedRooms } from 'pages/dimensions/helpers/get-calculated-rooms.helper';
import { css } from 'linaria';
import { TRoomsParams } from 'common/types/floor-params.type';
import { useIsFloorCalculator } from 'pages/dimensions/hooks/use-is-floor-calculator.hook';
import { dimensionsErrors, dimensionsLabels } from '../../const';
import { ParametersEnum } from '../../types';
import { useFilteredInputParams } from '../hooks/use-filtered-input-params';
import { InputContainerColumn, InputContainerRow } from '../styles';
import { checkIsSumWrong, getAreaState } from './helpers';
import { useFormConditions } from '../../hooks/use-dimension-form-conditions';
import { AdaptiveInput } from '../../components/AdaptiveInput';
import { SHOULD_BE_MORE_THEN_ZERO } from '../const';

interface IRoomParametersForm {
  hasNegativeParams: boolean | undefined;
  setHasNegativeParams: Dispatch<SetStateAction<boolean | undefined>>;
  setShowWarningBar: Dispatch<SetStateAction<boolean>>;
}

export const RoomParametersForm = ({
  hasNegativeParams,
  setHasNegativeParams,
  setShowWarningBar,
}: IRoomParametersForm) => {
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    clearErrors,
  } = useFormContext();
  const { setContext: setRoute } = useContext(RouteContext);

  const isFloorCalculatorId = useIsFloorCalculator();

  const formValue = getValues();

  const { hasMaterialsHeader } = useFormConditions();

  const [isAreaEnabled, setIsAreaEnabled] = useRecoilState(roomArea);
  const [calcuLatedRoomParams, setCalcuLatedRoomParams] = useRecoilState<TRoomsParams>(
    calculatedRoomsParams
  );
  const { inputParameters, defaultInputParams, filteredSurfaces, hasLengthWidth } = useFilteredInputParams();

  // WALL case in AREA
  const hasNotWallArea = filteredSurfaces.some(
    surface => surface.name === 'wall' && !surface?.inputParameters.includes(ParametersEnum.area)
  );
  // has AREA without WALL and with LENGTH & WIDTH
  const hasArea = inputParameters.includes(ParametersEnum.area) && !hasNotWallArea && hasLengthWidth;

  const defaultState = getAreaState({
    inputParams: defaultInputParams,
    isEnabled: hasLengthWidth
      ? hasArea && isAreaEnabled
      : inputParameters.includes(ParametersEnum.area) && !hasNotWallArea,
  });

  const [filteredInputParams, setInputParams] = useState(defaultState);

  const calculatedRooms = getCalculatedRooms();

  const handleChange = (onChange: (value: string) => void) => (value: string) => {
    if (calcuLatedRoomParams.calculatorId.length) {
      // Сброс значения и выбраной кнопки при изменении
      setCalcuLatedRoomParams(defaultRoomParams);

      setValue(ParametersEnum.width, '');
      setValue(ParametersEnum.length, '');
      setValue(ParametersEnum.area, '');
    }

    onChange(value);
  };

  const handleClickRoomParamsBtn = (room: TRoomsParams) => {
    const isCalculatedRoomsParamsHasOnlySquare = !room.length && !!room.square;
    setValue(ParametersEnum.width, String(room.width ?? ''));
    setValue(ParametersEnum.length, String(room.length ?? ''));
    setValue(ParametersEnum.area, String(room.square ?? ''));

    setCalcuLatedRoomParams(room);
    changeInputsParams(isCalculatedRoomsParamsHasOnlySquare);
    setIsAreaEnabled(isCalculatedRoomsParamsHasOnlySquare);
    clearErrors([ParametersEnum.width, ParametersEnum.length, ParametersEnum.area]);
  };

  const changeInputsParams = (isEnabled: boolean) => {
    const areaState = getAreaState({
      inputParams: defaultInputParams,
      isEnabled,
    });

    setInputParams(areaState);
  };

  const onAreaButtonClick = () => {
    const areaEnabledState = !isAreaEnabled;
    setIsAreaEnabled(areaEnabledState);

    gaClickOnGoods({
      title: 'Параметры помещения',
      name: areaEnabledState ? 'Указать площадь' : 'Указать размеры',
    });
    changeInputsParams(areaEnabledState);
  };

  const onChangeSurfaceClick = () => {
    setRoute('surface');
  };

  const onInputFocus = (fieldName: string) => () => {
    gaFocusOnDimensions({ fieldName: dimensionsLabels[fieldName], title: 'Параметры помещения' });
  };

  const roomParamsQuantity =
    filteredInputParams.length > 3
      ? [
          { inputParameters: filteredInputParams.slice(0, 2), key: 'large_start' },
          { inputParameters: filteredInputParams.slice(2), key: 'large_end' },
        ]
      : [{ inputParameters: filteredInputParams, key: 'mobile' }];

  useEffect(() => {
    if (filteredInputParams.includes(ParametersEnum.area) && filteredInputParams.length === 1) {
      setIsAreaEnabled(true);
    }
  }, [filteredInputParams, setIsAreaEnabled]);

  useEffect(() => {
    setHasNegativeParams(checkIsSumWrong(formValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValue]);

  useEffect(() => {
    if (hasNegativeParams) {
      setShowWarningBar(true);
    } else {
      setShowWarningBar(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNegativeParams]);

  return (
    <>
      {calculatedRooms.length && isFloorCalculatorId ? (
        <RoomsParamsContainer>
          <Typography variant="l" color="text-primary" size="body_accent">
            Помещение
          </Typography>
          <CalculatedRoomsContainer>
            {calculatedRooms.map(room => {
              const formattedSquare =
                // eslint-disable-next-line no-nested-ternary
                room.square != null
                  ? room.square % 1 === 0
                    ? room.square.toFixed(0)
                    : room.square.toFixed(2)
                  : '—';

              return (
                <RegularButton
                  key={room.id}
                  variant={calcuLatedRoomParams.id === room.id ? 'primary' : 'secondary'}
                  className={calculatedRoomBtn}
                  onClick={() => handleClickRoomParamsBtn(room)}>
                  <Typography size="body_long" variant="s">
                    Комната {formattedSquare} м²
                  </Typography>
                </RegularButton>
              );
            })}
          </CalculatedRoomsContainer>
        </RoomsParamsContainer>
      ) : null}

      <ButtonContainer>
        <HeaderContainer>
          <Typography variant="l" color="text-primary" size="body_accent">
            Параметры помещения
          </Typography>
          {hasMaterialsHeader && (
            <Adaptive to="tablet">
              <Button variant="pseudo" size="s" label="Изменить параметры" onClick={onChangeSurfaceClick}>
                <Pencil />
              </Button>
            </Adaptive>
          )}
        </HeaderContainer>
        {hasArea && (
          <Button
            variant="outline"
            label={isAreaEnabled ? 'Указать размеры' : 'Указать площадь'}
            size="s"
            onClick={onAreaButtonClick}>
            {isAreaEnabled ? 'Указать размеры' : 'Указать площадь'}
          </Button>
        )}
      </ButtonContainer>
      {roomParamsQuantity.map((roomsParams, i) => (
        <InputsContainer last={i + 1 === roomParamsQuantity.length} key={roomsParams.key}>
          <InputContainerRow key={roomsParams.key}>
            {roomsParams.inputParameters.map((parameter, idx) => (
              <InputContainerColumn key={parameter} last={roomsParams.inputParameters.length === idx + 1}>
                <Controller
                  name={parameter}
                  control={control}
                  rules={{
                    validate: {
                      required: value => {
                        if (value === '0') return SHOULD_BE_MORE_THEN_ZERO;
                        if (!value) return dimensionsErrors[parameter]?.required;
                        return undefined;
                      },
                    },
                    pattern: {
                      value: dimensionsErrors[parameter]?.value,
                      message: dimensionsErrors[parameter]?.pattern,
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <Adaptive to="tablet">
                        <AdaptiveInput
                          form={{
                            value,
                            onChange: handleChange(onChange),
                            onFocus: onInputFocus(parameter),
                            errors,
                          }}
                          name={parameter}
                          locale={{ labels: dimensionsLabels }}
                          mobile
                        />
                      </Adaptive>
                      <Adaptive from="tablet">
                        <AdaptiveInput
                          form={{
                            value,
                            onChange: handleChange(onChange),
                            onFocus: onInputFocus(parameter),
                            errors,
                          }}
                          name={parameter}
                          locale={{ labels: dimensionsLabels }}
                          mobile={false}
                        />
                      </Adaptive>
                    </>
                  )}
                />
              </InputContainerColumn>
            ))}
          </InputContainerRow>
        </InputsContainer>
      ))}
    </>
  );
};

const ButtonContainer = styled.div`
  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: 8px;
  }
`;

const HeaderContainer = styled.div`
  @media (max-width: 767px) {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
`;

const InputsContainer = styled.div<{ last: boolean }>`
  margin-top: 24px;

  @media (max-width: 767px) {
    margin-top: ${props => (props.last ? '28px' : '24px')};
  }
`;

const RoomsParamsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-150);
  margin-bottom: var(--space-400);
`;

const calculatedRoomBtn = css`
  min-width: 137px;
`;

const CalculatedRoomsContainer = styled.div`
  display: flex;
  gap: var(--space-100);

  @media (max-width: 767px) {
    flex-wrap: wrap;
  }
`;
